/**
 * Les appels des liens de suivi, côté exploitation — lot 162, 04/11/2026.
 * Routes : `apps/api/src/liens_suivi.ts`.
 */

import { appeler, echec, type Resultat } from './api.js';
import {
  lignesChargees,
  quantitesLivrees,
  type ExecutionArret,
  type ExecutionDeLettre,
  type LettreVoiture,
} from './lettreVoiture.js';

export interface LienSuivi {
  readonly id: string;
  readonly genre: 'client' | 'chauffeur';
  readonly cree_le: string;
  readonly cree_par: string;
  /** Lot 163 : le nom donné par l'exploitation pour un lien chauffeur. */
  readonly chauffeur_nom: string | null;
  /** Recalculé par l'API à chaque lecture : la base n'en garde que l'empreinte. */
  readonly code: string;
}

type Liens = { readonly liens: readonly LienSuivi[] };

export async function lireLesLiens(jeton: string, otId: string): Promise<Resultat<readonly LienSuivi[]>> {
  const e = await appeler(`/ordres-transport/${otId}/liens-suivi`, jeton);
  if (e.etat !== 'succes') return echec(e);
  return { etat: 'ok', valeur: (e.corps as Liens).liens };
}

export async function creerLeLienClient(jeton: string, otId: string): Promise<Resultat<readonly LienSuivi[]>> {
  const e = await appeler(`/ordres-transport/${otId}/liens-suivi`, jeton, {
    methode: 'POST',
    corps: { genre: 'client' },
  });
  if (e.etat !== 'succes') return echec(e);
  return { etat: 'ok', valeur: (e.corps as Liens).liens };
}

export async function couperLeLien(
  jeton: string,
  otId: string,
  lienId: string,
): Promise<Resultat<readonly LienSuivi[]>> {
  const e = await appeler(`/ordres-transport/${otId}/liens-suivi/${lienId}/couper`, jeton, { methode: 'POST' });
  if (e.etat !== 'succes') return echec(e);
  return { etat: 'ok', valeur: (e.corps as Liens).liens };
}

/** L'adresse que l'on donne au client : le code dans l'ancre, jamais envoyé au serveur des pages. */
export function adresseDuSuivi(origine: string, code: string): string {
  return `${origine}/suivi.html#${code}`;
}

/** L'adresse de la mission du chauffeur (lot 163), même règle que le suivi. */
export function adresseDeLaMission(origine: string, code: string): string {
  return `${origine}/mission.html#${code}`;
}

export async function creerLeLienChauffeur(
  jeton: string,
  otId: string,
  chauffeurNom: string,
): Promise<Resultat<readonly LienSuivi[]>> {
  const e = await appeler(`/ordres-transport/${otId}/liens-suivi`, jeton, {
    methode: 'POST',
    corps: { genre: 'chauffeur', chauffeur_nom: chauffeurNom },
  });
  if (e.etat !== 'succes') return echec(e);
  return { etat: 'ok', valeur: (e.corps as Liens).liens };
}

/**
 * Ce que le chauffeur a fait : plaques, arrivées, départs (lot 163) ;
 * marchandise, réserves, signatures (lot 164).
 */
export interface ExecutionLue {
  readonly plaques: { readonly tracteur: string; readonly remorque: string | null; readonly commence_le: string } | null;
  /** Le chauffeur du dernier lien créé. */
  readonly chauffeur: string | null;
  readonly etapes: readonly (ExecutionArret & {
    readonly etape_id: string;
    readonly arrive_gps: boolean;
  })[];
}

/** Ce qui remplit les cases du chauffeur d'une lettre, côté exploitation. */
export function executionDeLaLettre(
  x: ExecutionLue | null,
  l: Pick<LettreVoiture, 'etape_chargement_id' | 'etape_livraison_id'>,
): ExecutionDeLettre | null {
  if (x === null) return null;
  const arret = (id: string) => x.etapes.find((e) => e.etape_id === id) ?? null;
  return { chauffeur: x.chauffeur, plaques: x.plaques, chargement: arret(l.etape_chargement_id), livraison: arret(l.etape_livraison_id) };
}

/**
 * L'état d'une lettre, comme la maquette du 04/11. « Avec réserve » : une
 * réserve écrite, ou une ligne livrée en moins.
 */
export function etatDeLaLettre(l: Pick<LettreVoiture, 'etape_chargement_id' | 'etape_livraison_id' | 'contenu'>, ex: ExecutionDeLettre | null): string {
  const liv = ex?.livraison ?? null;
  if (liv !== null && liv.signe_le !== null) {
    const livrees = quantitesLivrees(l, ex);
    const manque = livrees !== null && lignesChargees(l, ex).some((x, i) => livrees[i] !== undefined && livrees[i] !== Number(x.quantite));
    return liv.reserve_client !== null || liv.reserve_chauffeur !== null || manque ? 'Livrée avec réserve' : 'Signée et livrée';
  }
  if (ex?.chargement != null && ex.chargement.signe_le !== null) return 'En route — signée au chargement';
  return 'Générée — attend le chargement';
}

export async function lireLExecution(jeton: string, otId: string): Promise<Resultat<ExecutionLue>> {
  const e = await appeler(`/ordres-transport/${otId}/execution`, jeton);
  if (e.etat !== 'succes') return echec(e);
  return { etat: 'ok', valeur: e.corps as ExecutionLue };
}
